import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import toast from 'react-hot-toast';

interface Coupon {
  id: string;
  code: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  is_downloaded: boolean;
  user_id: string | null;
}

export default function Coupons() {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [purchasedCoupons, setPurchasedCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const user = getCurrentUser();
  const navigate = useNavigate();

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      // Load available coupons
      const { data: availableCoupons, error: availableError } = await supabase
        .from('stripecoupons')
        .select('*')
        .is('user_id', null)
        .order('created_at', { ascending: false });

      if (availableError) throw availableError;

      // Load user's purchased coupons
      const { data: userCoupons, error: userError } = await supabase
        .from('stripecoupons')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (userError) throw userError;

      setCoupons(availableCoupons || []);
      setPurchasedCoupons(userCoupons || []);
    } catch (error: any) {
      toast.error('Error loading coupons');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (coupon: Coupon) => {
    if (!user) {
      toast.error('Please log in to purchase coupons');
      return;
    }

    setProcessing(true);
    try {
      // Get user's current balance
      const { data: userData } = await supabase
        .from('stripeusers')
        .select('wallet_balance')
        .eq('id', user.id)
        .single();

      if (!userData) throw new Error('User not found');
      if (userData.wallet_balance < coupon.price) {
        throw new Error('Insufficient balance');
      }

      // Start a transaction
      const { data: existingUsage } = await supabase
        .from('stripecouponusage')
        .select('valid_until')
        .eq('user_id', user.id)
        .gte('valid_until', new Date().toISOString())
        .single();

      // Calculate new validity period
      const currentDate = new Date();
      const validUntil = existingUsage
        ? new Date(existingUsage.valid_until)
        : currentDate;
      validUntil.setDate(validUntil.getDate() + 15); // Add 15 days

      // Update coupon ownership
      const { error: couponError } = await supabase
        .from('stripecoupons')
        .update({ user_id: user.id })
        .eq('id', coupon.id);

      if (couponError) throw couponError;

      // Create or update coupon usage
      const { error: usageError } = await supabase
        .from('stripecouponusage')
        .upsert({
          user_id: user.id,
          coupon_id: coupon.id,
          valid_until: validUntil.toISOString()
        });

      if (usageError) throw usageError;

      // Update user's balance
      const { error: updateError } = await supabase
        .from('stripeusers')
        .update({ 
          wallet_balance: userData.wallet_balance - coupon.price 
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Create withdraw transaction
      const { error: transactionError } = await supabase
        .from('stripetransactions')
        .insert({
          user_id: user.id,
          amount: coupon.price,
          type: 'withdraw',
          status: 'completed'
        });

      if (transactionError) throw transactionError;

      // Navigate to confirmation page
      navigate('/coupon-confirmation', { 
        state: { 
          coupon: {
            code: coupon.code,
            name: coupon.name,
            description: coupon.description,
            price: coupon.price
          }
        }
      });
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = async (coupon: Coupon) => {
    try {
      // Update download status
      const { error } = await supabase
        .from('stripecoupons')
        .update({ is_downloaded: true })
        .eq('id', coupon.id);

      if (error) throw error;

      // Create download link
      const element = document.createElement('a');
      const file = new Blob([
        `Your Song Access Coupon\n\n` +
        `Code: ${coupon.code}\n` +
        `Valid for: ${coupon.name}\n` +
        `${coupon.description}\n\n` +
        `This coupon provides 15 days of unlimited access to all songs and learning materials.\n\n` +
        `Your access has been automatically activated.\n` +
        `You can now enjoy learning languages through music!\n\n` +
        `Need help? Contact support@amalie.com`
      ], { type: 'text/plain' });
      
      element.href = URL.createObjectURL(file);
      element.download = `song-access-coupon-${coupon.code}.txt`;
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);

      toast.success('Coupon downloaded successfully!');
      loadCoupons(); // Reload to update download status
    } catch (error: any) {
      toast.error('Error downloading coupon');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-600">Loading coupons...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Song Access Coupons</h1>
            <p className="mt-1 text-sm text-gray-500">
              Purchase coupons to access all songs and learning materials
            </p>
          </div>
          <Link
            to="/dashboard"
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Dashboard
          </Link>
        </div>

        {/* Available Coupons */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Available Coupons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coupons.map((coupon) => (
              <div key={coupon.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="h-48 w-full overflow-hidden bg-gray-200">
                  <img
                    src={coupon.image_url}
                    alt={coupon.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{coupon.name}</h3>
                  <p className="text-gray-600 mb-4">{coupon.description}</p>
                  <div className="mb-4">
                    <span className="text-lg font-mono bg-gray-100 px-3 py-1 rounded">
                      {coupon.code.substring(0, 2)}xxxxxx
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-2xl font-bold text-gray-900">
                      ${coupon.price.toFixed(2)}
                    </p>
                    <button
                      onClick={() => handlePurchase(coupon)}
                      disabled={processing}
                      className="bg-black text-white px-4 py-2 rounded-md hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black disabled:opacity-50"
                    >
                      {processing ? 'Processing...' : 'Buy Now'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Purchased Coupons */}
        {purchasedCoupons.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Coupons</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {purchasedCoupons.map((coupon) => (
                <div key={coupon.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
                  <div className="h-48 w-full overflow-hidden bg-gray-200">
                    <img
                      src={coupon.image_url}
                      alt={coupon.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{coupon.name}</h3>
                    <p className="text-gray-600 mb-4">{coupon.description}</p>
                    <div className="mb-4">
                      <span className="text-lg font-mono bg-gray-100 px-3 py-1 rounded">
                        {coupon.code}
                      </span>
                    </div>
                    <button
                      onClick={() => handleDownload(coupon)}
                      disabled={coupon.is_downloaded}
                      className={`w-full py-2 px-4 rounded-md ${
                        coupon.is_downloaded
                          ? 'bg-gray-200 text-gray-600 cursor-not-allowed'
                          : 'bg-black text-white hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black'
                      }`}
                    >
                      {coupon.is_downloaded ? 'Downloaded' : 'Download Coupon'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}